/**
 * MyEstimatesPage — personal "what's on my plate" dashboard for any
 * logged-in user who is linked to a named_resource.
 *
 * Pairs with the v260.81 user_id FK: tenant admin promotes a user via
 * Settings → Named Resources, and the moment that user logs in they
 * get a curated view of every active estimate that carries hours for
 * their role (explicit assignments first, divide-equally fallback
 * second). No admin role required.
 */
import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import Sparkline from '../components/Sparkline';

// Utility: snap an arbitrary ISO date string to the Monday of that ISO
// week. Mirrors the backend's _normalise_blocked_weeks helper.
const snapMondayIso = (iso) => {
  if (!iso) return null;
  const d = new Date(iso + 'T00:00:00Z');
  if (Number.isNaN(d.getTime())) return null;
  const dow = d.getUTCDay(); // 0 = Sun, 1 = Mon, ..., 6 = Sat
  const diff = dow === 0 ? -6 : 1 - dow;
  d.setUTCDate(d.getUTCDate() + diff);
  return d.toISOString().slice(0, 10);
};
import {
  Loader2, UserCircle2, AlertCircle, ExternalLink, Briefcase, Clock,
  TrendingUp, Layers, AlertTriangle, Search, Link as LinkIcon, Mail,
  CalendarOff, X as XIcon, Plus,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Reuse the Roster heatmap's load thresholds so the colour story is
// consistent across the app. (See RosterView.js.)
const loadBucketFor = (weeks) => {
  const w = weeks == null ? 0 : weeks;
  if (w <= 0.25) return { id: 'idle', label: 'Idle', text: 'text-slate-500', chip: 'bg-slate-100 text-slate-600' };
  if (w <= 2)    return { id: 'healthy', label: 'Healthy', text: 'text-emerald-700', chip: 'bg-emerald-100 text-emerald-700' };
  if (w <= 4)    return { id: 'loaded', label: 'Loaded', text: 'text-amber-700', chip: 'bg-amber-100 text-amber-700' };
  return { id: 'overbooked', label: 'Overbooked', text: 'text-rose-700', chip: 'bg-rose-100 text-rose-700' };
};

export default function MyEstimatesPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [search, setSearch] = useState('');
  const [viaFilter, setViaFilter] = useState('all'); // 'all' | 'explicit' | 'fallback'
  const [newBlockDate, setNewBlockDate] = useState('');
  const [savingBlocks, setSavingBlocks] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API_URL}/api/my-estimates`, { withCredentials: true })
      .then(r => { if (!cancelled) setData(r.data); })
      .catch(e => { if (!cancelled) setError(e?.response?.data?.detail || e.message || 'Failed to load'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    if (!data?.assignments) return [];
    const q = search.trim().toLowerCase();
    return data.assignments.filter(a => {
      if (viaFilter !== 'all' && a.via !== viaFilter) return false;
      if (!q) return true;
      return (a.estimate_name || '').toLowerCase().includes(q)
        || (a.organization_name || '').toLowerCase().includes(q);
    });
  }, [data, search, viaFilter]);

  const blockedWeeks = data?.sparkline_meta?.user_blocked_weeks || [];

  const saveBlockedWeeks = async (nextList) => {
    setSavingBlocks(true);
    try {
      const r = await axios.put(
        `${API_URL}/api/my-estimates/blocked-weeks`,
        { blocked_weeks: nextList },
        { withCredentials: true }
      );
      // Re-fetch the whole dashboard so the sparkline + KPIs reflect the change.
      const refreshed = await axios.get(`${API_URL}/api/my-estimates`, { withCredentials: true });
      setData(refreshed.data);
      return r.data;
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e?.response?.data?.detail || 'Failed to save blocked weeks');
    } finally {
      setSavingBlocks(false);
    }
  };

  const addBlockWeek = async () => {
    if (!newBlockDate) return;
    const monday = snapMondayIso(newBlockDate);
    if (!monday) return;
    if (blockedWeeks.includes(monday)) {
      setNewBlockDate('');
      return;
    }
    await saveBlockedWeeks([...blockedWeeks, monday]);
    setNewBlockDate('');
  };

  const removeBlockWeek = async (iso) => {
    await saveBlockedWeeks(blockedWeeks.filter(w => w !== iso));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="my-estimates-loading">
        <Loader2 className="w-6 h-6 animate-spin text-[#0F2040]" />
      </div>
    );
  }

  if (error) {
    return (
      <Card className="max-w-3xl mx-auto mt-12" data-testid="my-estimates-error">
        <CardContent className="py-10 text-center">
          <AlertCircle className="w-8 h-8 mx-auto text-rose-500 mb-2" />
          <p className="text-sm text-rose-700">{error}</p>
        </CardContent>
      </Card>
    );
  }

  // ── Not linked: prompt the user to ask an admin to promote them ──
  if (!data?.linked) {
    return (
      <div className="max-w-3xl mx-auto p-6" data-testid="my-estimates-not-linked">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <UserCircle2 className="w-5 h-5 text-[#4ABFCF]" />
              My estimates
            </CardTitle>
          </CardHeader>
          <CardContent className="py-6 text-center">
            <UserCircle2 className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <p className="text-base font-medium text-[#0F2040]">You're not on the named-resource roster yet.</p>
            <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto">
              Ask an admin to promote your account in <strong>Settings → Named Resources</strong>. Once linked, every active estimate that carries hours for your role will appear here automatically.
            </p>
            {data?.user?.email && (
              <p className="text-xs text-slate-400 mt-4">
                Signed in as <strong>{data.user.email}</strong> · share this email with your admin to speed up the promotion.
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── Linked: full dashboard ──
  const r = data.resource;
  const bucket = loadBucketFor(data.weeks_of_work);

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-4" data-testid="my-estimates-page">
      {/* Header card */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <UserCircle2 className="w-5 h-5 text-[#4ABFCF]" />
                {r.name}
                <Badge variant="secondary" className="text-[10px]" data-testid="my-estimates-role-badge">
                  {r.role}
                </Badge>
                <Badge className="bg-indigo-100 text-indigo-700 border-indigo-200 text-[10px]">
                  <LinkIcon className="w-2.5 h-2.5 mr-0.5 inline" />
                  Linked
                </Badge>
              </CardTitle>
              <div className="flex items-center gap-3 mt-1 text-xs text-slate-500 flex-wrap">
                {r.email && (
                  <a href={`mailto:${r.email}`} className="hover:text-[#4ABFCF] inline-flex items-center gap-1">
                    <Mail className="w-3 h-3" />{r.email}
                  </a>
                )}
                <span>·</span>
                <span>{r.weekly_capacity_hours}h / week capacity</span>
                {data.peers_in_role > 1 && (
                  <>
                    <span>·</span>
                    <span>{data.peers_in_role - 1} peer{data.peers_in_role > 2 ? 's' : ''} in role</span>
                  </>
                )}
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* KPI row */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="border border-slate-100 rounded-lg p-3" data-testid="my-estimates-kpi-explicit">
              <div className="text-[10px] uppercase tracking-wide text-slate-400">Explicit assignments</div>
              <div className="text-2xl font-semibold text-[#0F2040] tabular-nums">{data.explicit_count}</div>
              <div className="text-[10px] text-slate-400">estimate{data.explicit_count === 1 ? '' : 's'} where you're picked</div>
            </div>
            <div className="border border-slate-100 rounded-lg p-3" data-testid="my-estimates-kpi-fallback">
              <div className="text-[10px] uppercase tracking-wide text-slate-400">Divide-equally fallback</div>
              <div className="text-2xl font-semibold text-[#0F2040] tabular-nums">{data.fallback_count}</div>
              <div className="text-[10px] text-slate-400">estimate{data.fallback_count === 1 ? '' : 's'} via auto-split</div>
            </div>
            <div className="border border-slate-100 rounded-lg p-3" data-testid="my-estimates-kpi-hours">
              <div className="text-[10px] uppercase tracking-wide text-slate-400">Total allocated</div>
              <div className="text-2xl font-semibold text-[#0F2040] tabular-nums">{data.total_hours.toLocaleString()}h</div>
              <div className="text-[10px] text-slate-400">across all active estimates</div>
            </div>
            <div className="border border-slate-100 rounded-lg p-3" data-testid="my-estimates-kpi-weeks">
              <div className="text-[10px] uppercase tracking-wide text-slate-400">Weeks of work</div>
              <div className={`text-2xl font-semibold tabular-nums ${bucket.text}`}>
                {data.weeks_of_work?.toFixed(1) ?? '—'}w
                {bucket.id === 'overbooked' && <AlertTriangle className="w-4 h-4 inline ml-1 -mt-1" />}
              </div>
              <Badge className={`text-[9px] mt-0.5 ${bucket.chip}`}>{bucket.label}</Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sparkline card — surfaces the exact week the user tips into overbooked */}
      {data.weekly_hours && data.weekly_hours.length > 0 && (
        <Card data-testid="my-estimates-sparkline-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-[#4ABFCF]" />
              Weekly load — next {data.sparkline_meta?.weeks || data.weekly_hours.length} weeks
            </CardTitle>
            <p className="text-[11px] text-slate-400">
              Bars colour-coded by ratio of allocated hours to your weekly capacity ({r.weekly_capacity_hours}h). Dashed line = capacity, navy line = today, hover any bar for the exact week.
            </p>
          </CardHeader>
          <CardContent>
            <Sparkline
              weeklyHours={data.weekly_hours}
              capacityPerWeek={r.weekly_capacity_hours || 40}
              weekLabels={data.week_labels}
              todayIdx={data.sparkline_meta?.today_idx ?? 0}
              blockedIndices={data.sparkline_meta?.blocked_week_indices || []}
              height={64}
              testid="my-estimates-sparkline"
            />
            {(() => {
              const peak = Math.max(...data.weekly_hours);
              const peakIdx = data.weekly_hours.indexOf(peak);
              const cap = r.weekly_capacity_hours || 40;
              const firstOverbookedIdx = data.weekly_hours.findIndex(h => h > cap);
              return (
                <div className="flex items-center justify-between gap-3 mt-2 text-[11px] text-slate-500 flex-wrap">
                  <span data-testid="my-estimates-sparkline-peak">
                    Peak {peak.toFixed(0)}h in week of <strong>{(data.week_labels?.[peakIdx] || '—').slice(5)}</strong>
                  </span>
                  {firstOverbookedIdx >= 0 ? (
                    <span className="text-rose-600 font-medium flex items-center gap-1" data-testid="my-estimates-sparkline-first-over">
                      <AlertTriangle className="w-3 h-3" />
                      First overbooked week: <strong>{(data.week_labels?.[firstOverbookedIdx] || '—').slice(5)}</strong>
                    </span>
                  ) : (
                    <span className="text-emerald-700 font-medium" data-testid="my-estimates-sparkline-safe">
                      You stay under capacity for the whole window.
                    </span>
                  )}
                </div>
              );
            })()}
          </CardContent>
        </Card>
      )}
      {data.sparkline_meta?.skipped_reason && (
        <Card className="border-amber-200 bg-amber-50/40" data-testid="my-estimates-sparkline-skipped">
          <CardContent className="py-3 text-xs text-amber-800 flex items-center gap-2">
            <AlertTriangle className="w-3.5 h-3.5" />
            <span>Sparkline skipped: {data.sparkline_meta.skipped_reason}</span>
          </CardContent>
        </Card>
      )}

      {/* Block weeks editor — PTO / vacation self-service */}
      <Card data-testid="my-estimates-blocked-weeks-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <CalendarOff className="w-4 h-4 text-[#4ABFCF]" />
            Block weeks
            {blockedWeeks.length > 0 && (
              <Badge variant="secondary" className="text-[10px]" data-testid="my-estimates-blocked-count">
                {blockedWeeks.length} blocked
              </Badge>
            )}
          </CardTitle>
          <p className="text-[11px] text-slate-400">
            Mark PTO / vacation weeks. Your sparkline greys those weeks out and your peers absorb your residual share of any role hours during them. Stored Monday-aligned (any weekday you pick snaps to that week's Monday).
          </p>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 flex-wrap" data-testid="my-estimates-blocked-add">
            <Input
              type="date"
              value={newBlockDate}
              onChange={(e) => setNewBlockDate(e.target.value)}
              className="w-44 h-8 text-sm"
              disabled={savingBlocks}
              data-testid="my-estimates-blocked-input"
              placeholder="Pick any weekday"
            />
            <Button
              size="sm"
              onClick={addBlockWeek}
              disabled={!newBlockDate || savingBlocks}
              className="bg-[#0F2040] hover:bg-[#1A3055] text-white"
              data-testid="my-estimates-blocked-add-btn"
            >
              {savingBlocks ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
              Block week
            </Button>
            {blockedWeeks.length > 0 && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => saveBlockedWeeks([])}
                disabled={savingBlocks}
                className="text-rose-600 hover:bg-rose-50"
                data-testid="my-estimates-blocked-clear"
              >
                Clear all
              </Button>
            )}
          </div>
          {blockedWeeks.length === 0 ? (
            <p className="text-[11px] text-slate-400 italic mt-3" data-testid="my-estimates-blocked-empty">
              No weeks blocked. Add one to start protecting your calendar.
            </p>
          ) : (
            <div className="flex flex-wrap gap-1.5 mt-3" data-testid="my-estimates-blocked-list">
              {blockedWeeks.map(iso => (
                <span
                  key={iso}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-[11px]"
                  data-testid={`my-estimates-blocked-chip-${iso}`}
                >
                  <CalendarOff className="w-2.5 h-2.5" />
                  Week of {iso.slice(5)}
                  <button
                    type="button"
                    onClick={() => removeBlockWeek(iso)}
                    disabled={savingBlocks}
                    className="hover:text-rose-600 ml-0.5"
                    title="Unblock"
                    data-testid={`my-estimates-blocked-remove-${iso}`}
                  >
                    <XIcon className="w-2.5 h-2.5" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Filters */}
      {data.assignments.length > 0 && (
        <Card>
          <CardContent className="py-3">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Filter by estimate name or organisation…"
                  className="pl-8 h-8 text-sm"
                  data-testid="my-estimates-search"
                />
              </div>
              <div className="flex items-center gap-1">
                {[
                  { id: 'all',      label: 'All' },
                  { id: 'explicit', label: 'Explicit' },
                  { id: 'fallback', label: 'Auto-split' },
                ].map(t => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setViaFilter(t.id)}
                    data-testid={`my-estimates-via-${t.id}`}
                    className={`px-3 py-1 rounded-full text-[11px] border transition ${
                      viaFilter === t.id
                        ? 'bg-[#0F2040] text-white border-[#0F2040]'
                        : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Assignment list */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Briefcase className="w-4 h-4 text-[#4ABFCF]" />
            What's on your plate
          </CardTitle>
        </CardHeader>
        <CardContent>
          {data.assignments.length === 0 ? (
            <p className="text-sm text-slate-500 py-6 text-center" data-testid="my-estimates-empty">
              No estimates currently carry hours for the <strong>{r.role}</strong> role. Anything you don't see here either isn't active or doesn't need a {r.role}.
            </p>
          ) : filtered.length === 0 ? (
            <p className="text-sm text-slate-400 italic py-6 text-center" data-testid="my-estimates-filter-empty">
              Nothing matches the current filter.
            </p>
          ) : (
            <div className="space-y-1.5">
              {filtered.map(a => (
                <button
                  key={`${a.estimate_id}-${a.via}`}
                  type="button"
                  onClick={() => navigate(`/planner?id=${a.estimate_id}`)}
                  className="w-full text-left border border-slate-100 hover:border-[#4ABFCF]/40 hover:bg-[#4ABFCF]/5 rounded-lg p-3 transition flex items-center gap-3"
                  data-testid={`my-estimates-row-${a.estimate_id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-sm text-[#0F2040] truncate flex items-center gap-1.5">
                      <ExternalLink className="w-3 h-3 text-slate-400 flex-shrink-0" />
                      {a.estimate_name}
                    </div>
                    <div className="text-[11px] text-slate-500 truncate">
                      {a.organization_name && <span>{a.organization_name} · </span>}
                      Role total: {a.role_total_hours.toLocaleString()}h
                      {a.via === 'fallback' && a.peers_in_role > 1 && (
                        <span> · split 1/{a.peers_in_role} across peers</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {a.via === 'explicit' ? (
                      <Badge
                        className="bg-indigo-100 text-indigo-700 border-indigo-200 text-[10px]"
                        data-testid={`my-estimates-explicit-${a.estimate_id}`}
                      >
                        {a.percentage}%
                      </Badge>
                    ) : (
                      <Badge
                        variant="secondary"
                        className="text-[10px] text-slate-500"
                        data-testid={`my-estimates-fallback-${a.estimate_id}`}
                      >
                        auto
                      </Badge>
                    )}
                    <div className="text-right" style={{ minWidth: 70 }}>
                      <div className="text-sm font-semibold tabular-nums text-[#0F2040]">{a.hours_share.toLocaleString()}h</div>
                      <div className="text-[10px] text-slate-400 tabular-nums">{(a.hours_share / (r.weekly_capacity_hours || 40)).toFixed(1)}w</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
          {filtered.length > 0 && (
            <div className="text-[10px] text-slate-400 mt-2" data-testid="my-estimates-count">
              Showing {filtered.length} of {data.assignments.length}
              {viaFilter !== 'all' && ` (filter: ${viaFilter})`}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
